import { Button, TableItem, TableMessage } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { Table } from 'components/organisms';
import { useFormikContext } from 'formik';
import { IFormikContextType } from 'hooks/useFormikField';
import { useNewWindowNavigate } from 'hooks/useNewWindowNavigate';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { PiPlus, PiTrash } from 'react-icons/pi';
import { useNavigate } from 'react-router-dom';
import { IAccountMutationForm } from 'services/accounts/types';
import {
  useGetSmartlinkBoards,
  useGetSmartlinkDomains,
} from 'services/smartlink';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDateToInput } from 'utils/global/date.utils';

interface IAccountSmartLinkDomainsSeletcProps {
  id: number;
  domainId: string;
  domainName: string;
  boardId?: string;
  boardName?: string;
}

const AccountSmartLinkDomainsSeletc = ({
  id,
  domainId,
  domainName,
  boardId,
}: IAccountSmartLinkDomainsSeletcProps) => {
  const { setFieldValue }: IFormikContextType<IAccountMutationForm> =
    useFormikContext<IAccountMutationForm>();
  const { t } = useTranslation();

  const smartlinkDomains = useGetSmartlinkDomains(boardId ?? '');

  const contractOptions =
    smartlinkDomains.data?.data.map(domain => ({
      value: domain.id,
      label: domain.name,
    })) ?? [];

  useEffect(() => {
    smartlinkDomains.refetch();
  }, [boardId]);

  return (
    <FormInput.Select
      name={`smartlinkDomains[${id}].id`}
      isAsync
      isSearchable
      label={t(`input.label.contract`)}
      onChange={value => {
        setFieldValue(`smartlinkDomains[${id}].name`, value.label);
      }}
      value={{
        label: domainName,
        value: domainId,
      }}
      options={contractOptions}
      isLoading={smartlinkDomains.isLoading}
    />
  );
};

const AccountSmartLinkDomains = () => {
  const {
    values,
    status,
    setFieldValue,
    initialValues,
  }: IFormikContextType<IAccountMutationForm> =
    useFormikContext<IAccountMutationForm>();
  const { t } = useTranslation();
  const navigateToNewWindow = useNewWindowNavigate();
  const navigate = useNavigate();

  const boards = useGetSmartlinkBoards();

  const getLabelOptions = () =>
    boards.data?.data.map(label => ({ value: label.id, label: label.name })) ??
    [];

  const nodes = values.smartlinkDomains.map((domain, index) => ({
    ...domain,
    id: index,
    domainName: domain.name,
    domainId: domain.id,
  }));

  const onAddContract = () => {
    setFieldValue(`smartlinkDomains[${values.smartlinkDomains.length}]`, {
      contractId: '',
      contractName: '',
      labelId: '',
      labelName: '',
      startDate: formatDateToInput(new Date()),
      endDate: null,
      divisionPercentage: null,
    });
  };

  const onRemoveContract = (index: number) => {
    setFieldValue(
      'smartlinkDomains',
      values.smartlinkDomains.filter((_, key) => key !== index)
    );
  };

  return (
    <Table
      id={'AccountSmartLinkDomains'}
      size='compact'
      data={{ nodes: nodes }}
      columns={[
        {
          label: `${t('input.label.global')}`,
          renderCell: ({ boardName: name, boardId: id, id: index }) => (
            <TableItem>
              {status?.displayMode ? (
                <TableItem.Text text={name} />
              ) : (
                <FormInput.Select
                  name={`smartlinkDomains[${index}].boardId`}
                  isAsync
                  isSearchable
                  label={'Board'}
                  onChange={value => {
                    setFieldValue(
                      `smartlinkDomains[${index}].boardName`,
                      value.label
                    );
                    setFieldValue(`smartlinkDomains[${index}].name`, ' ');
                    setFieldValue(`smartlinkDomains[${index}].id`, ' ');
                  }}
                  value={{
                    label: name ?? '',
                    value: id ?? '',
                  }}
                  options={getLabelOptions()}
                  isLoading={boards.isLoading}
                />
              )}
            </TableItem>
          ),
          resize: true,
        },
        {
          label: t(`input.label.contract`),
          renderCell: domain => (
            <TableItem>
              {status?.displayMode ? (
                <TableItem.Text text={domain.domainName} />
              ) : !domain.boardId?.length ? (
                <FormInput.Select
                  name={`smartlinkDomains[${domain.id}].id`}
                  value={{ label: domain.domainName, value: domain.domainId }}
                  label={'Domain'}
                  isDisabled
                />
              ) : (
                <AccountSmartLinkDomainsSeletc {...domain} />
              )}
            </TableItem>
          ),
          resize: true,
        },

        {
          label: '',
          renderCell: ({ id: index }) => (
            <TableItem justifyPosition='end'>
              {status?.displayMode ? null : (
                <Button
                  variant={BUTTON_VARIANT.remove}
                  isClear
                  onlyIcon
                  onClick={() => onRemoveContract(index)}>
                  <Button.Icon icon={<PiTrash />} />
                </Button>
              )}
            </TableItem>
          ),
        },
      ]}
      tableOptions={
        status?.displayMode
          ? undefined
          : {
              renderAfterTable: () => (
                <tfoot className='full-width-footer'>
                  <TableMessage
                    className='cursor-pointer'
                    onClick={onAddContract}>
                    <TableItem.Text text={t('button.add')} />
                    <PiPlus />
                  </TableMessage>
                </tfoot>
              ),
            }
      }
    />
  );
};

export default AccountSmartLinkDomains;
